// NotificationPanel.js - Notifications panel component
import React, { useEffect, useState } from 'react';
import { Bell, Check, ExternalLink, Info, Gift, Shield, Trash2, RefreshCw } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const NotificationPanel = ({ onClose }) => {
  const {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    deleteNotification
  } = useNotifications();

  const { apiCall, token } = useAuth();
  const [filter, setFilter] = useState('all'); // all, unread
  const navigate = useNavigate();

  // Direct mark as read function with proper error handling
  const directMarkAsRead = async (notificationId) => {
    if (!token || !apiCall) {
      console.error('Authentication required to mark notification as read');
      return false;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh the notifications to see the change
        fetchNotifications(filter === 'unread');
        return true;
      } else {
        const error = await response.text();
        console.error('Failed to mark notification as read:', response.status, error);
        return false;
      }
    } catch (error) {
      console.error('Error marking notification as read:', error.message);
      return false;
    }
  };

  // Direct delete notification function with proper error handling
  const directDeleteNotification = async (notificationId) => {
    if (!token || !apiCall) {
      console.error('Authentication required to delete notification');
      return false;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh the notifications to see the change
        fetchNotifications(filter === 'unread');
        return true;
      } else {
        const error = await response.text();
        console.error('Failed to delete notification:', response.status, error);
        return false;
      }
    } catch (error) {
      console.error('Error deleting notification:', error.message);
      return false;
    }
  };

  useEffect(() => {
    // Fetch notifications when panel opens
    fetchNotifications(filter === 'unread');
  }, [filter, fetchNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return <Gift className="h-5 w-5 text-green-500" />;
      case 'security_alert':
        return <Shield className="h-5 w-5 text-red-500" />;
      case 'system_announcement':
        return <Info className="h-5 w-5 text-videbits-500" />;
      case 'account_update':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'new_content':
        return <Bell className="h-5 w-5 text-videbits-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationStyles = (notification) => {
    const baseStyles = "flex items-start space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-l-4 transition-colors";
    
    if (!notification.is_read) {
      return `${baseStyles} bg-videbits-50 border-l-videbits-500`;
    }
    
    return `${baseStyles} border-l-transparent`;
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread using direct function
    if (!notification.is_read) {
      await directMarkAsRead(notification.id);
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      if (notification.action_url.startsWith('http')) {
        window.open(notification.action_url, '_blank');
      } else {
        navigate(notification.action_url);
        onClose();
      }
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return time.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') {
      return !notification.is_read;
    }
    return true;
  });

  return (
    <div className="max-h-96 overflow-hidden flex flex-col">
      {/* Filter Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setFilter('all')}
          className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            filter === 'all'
              ? 'border-videbits-500 text-videbits-600'
              : 'border-transparent text-videbits-500 hover:text-videbits-700'
          }`}
        >
          All ({notifications.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            filter === 'unread'
              ? 'border-videbits-500 text-videbits-600'
              : 'border-transparent text-videbits-500 hover:text-videbits-700'
          }`}
        >
          Unread ({unreadCount})
        </button>
      </div>

      {/* Refresh Button */}
      <div className="p-2 border-b border-gray-200">
        <button
          onClick={() => fetchNotifications(filter === 'unread')}
          disabled={loading}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm text-videbits-600 hover:text-videbits-800 hover:bg-videbits-50 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-500"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Bell className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {filter === 'unread' 
                ? 'All caught up! Check back later for updates.'
                : 'We\'ll notify you when something important happens.'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={getNotificationStyles(notification)}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${
                        notification.is_read ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        notification.is_read ? 'text-gray-500' : 'text-gray-600'
                      }`}>
                        {notification.message}
                      </p>
                      
                      {/* Action Button */}
                      {notification.action_url && notification.action_text && (
                        <div className="mt-2">
                          <span className="inline-flex items-center space-x-1 text-xs text-videbits-600 hover:text-videbits-800">
                            <span>{notification.action_text}</span>
                            <ExternalLink className="h-3 w-3" />
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Unread indicator */}
                    {!notification.is_read && (
                      <div className="flex-shrink-0 ml-2">
                        <div className="h-2 w-2 bg-videbits-500 rounded-full"></div>
                      </div>
                    )}
                  </div>

                  {/* Timestamp and Actions */}
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {formatTimeAgo(notification.created_at)}
                    </span>

                    {/* Action buttons */}
                    <div className="flex items-center space-x-2">
                      {/* Mark as read button for unread notifications */}
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            directMarkAsRead(notification.id);
                          }}
                          className="text-xs text-videbits-600 hover:text-videbits-800 flex items-center space-x-1"
                          title="Mark as read"
                        >
                          <Check className="h-3 w-3" />
                          <span>Mark read</span>
                        </button>
                      )}

                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to delete this notification?')) {
                            directDeleteNotification(notification.id);
                          }
                        }}
                        className="text-xs text-red-600 hover:text-red-800 flex items-center space-x-1"
                        title="Delete notification"
                      >
                        <Trash2 className="h-3 w-3" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredNotifications.length > 0 && (
        <div className="border-t border-gray-200 p-3">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full text-center text-sm text-videbits-600 hover:text-videbits-800 font-medium"
          >
            View All Notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
